
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Filter as FilterIcon, Plus as PlusIcon, Search as SearchIcon } from "lucide-react";
import { toast } from "sonner";
import { CampaignsTable } from "@/components/campaigns/CampaignsTable";
import { PublishCampaignDialog } from "@/components/campaigns/PublishCampaignDialog";
import { DeleteCampaignDialog } from "@/components/campaigns/DeleteCampaignDialog";
import { Campaign, useCampaigns } from "@/hooks/useCampaigns";

export default function CampaignsList() {
  const [searchQuery, setSearchQuery] = useState("");
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [campaignToPublish, setCampaignToPublish] = useState<Campaign | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null);
  
  const { 
    campaigns, 
    isLoading, 
    error, 
    isProcessing,
    publishCampaign,
    deleteCampaign,
    duplicateCampaign
  } = useCampaigns();
  
  // Show error toast if fetch fails
  useEffect(() => {
    if (error) {
      toast.error("Failed to load campaigns");
      console.error("Error loading campaigns:", error);
    }
  }, [error]);
  
  const handlePublishCampaign = async () => {
    if (!campaignToPublish) return;
    
    const success = await publishCampaign(campaignToPublish);
    if (success) {
      setPublishDialogOpen(false);
      setCampaignToPublish(null);
    }
  };
  
  const handleDeleteCampaign = async () => {
    if (!campaignToDelete) return;
    
    const success = await deleteCampaign(campaignToDelete.id);
    if (success) {
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
    }
  };
  
  const openPublishDialog = (campaign: Campaign) => {
    setCampaignToPublish(campaign);
    setPublishDialogOpen(true);
  };
  
  const openDeleteDialog = (campaign: Campaign) => {
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
  };
  
  return (
    <AdminLayout 
      title="Campaigns" 
      subtitle="Manage your promotional campaigns"
    >
      <div className="space-y-4">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="relative max-w-sm">
            <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search campaigns..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="icon">
              <FilterIcon className="h-4 w-4" />
            </Button>
            <Button asChild>
              <Link to="/admin/campaigns/new">
                <PlusIcon className="mr-2 h-4 w-4" /> Create Campaign
              </Link>
            </Button>
          </div>
        </div>
        
        {/* Campaigns Table */}
        <CampaignsTable 
          campaigns={campaigns}
          isLoading={isLoading}
          searchQuery={searchQuery}
          onPublish={openPublishDialog}
          onDelete={openDeleteDialog}
          onDuplicate={duplicateCampaign}
        />
      </div>
      
      {/* Dialogs */}
      <PublishCampaignDialog 
        open={publishDialogOpen}
        onOpenChange={setPublishDialogOpen}
        campaign={campaignToPublish}
        isProcessing={isProcessing}
        onConfirm={handlePublishCampaign}
      />
      
      <DeleteCampaignDialog 
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        campaign={campaignToDelete}
        isProcessing={isProcessing}
        onConfirm={handleDeleteCampaign}
      />
    </AdminLayout>
  );
}
